import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getFunnel, createFunnel, updateFunnel } from "../../services/funnels";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const FunnelSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "Nome muito curto")
        .required("Obrigatório"),
});

const FunnelModal = ({ open, onClose, funnelId, reload }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        description: "",
    };

    const [funnel, setFunnel] = useState(initialState);

    useEffect(() => {
        const fetchFunnel = async () => {
            if (!funnelId) {
                setFunnel(initialState);
                return;
            }

            try {
                const { data } = await getFunnel(funnelId);
                setFunnel({
                    name: data.name || "",
                    description: data.description || "",
                });
            } catch (err) {
                toastError(err);
            }
        };
        fetchFunnel();
    }, [funnelId, open]);

    const handleClose = () => {
        setFunnel(initialState);
        onClose();
    };

    const handleSaveFunnel = async (values) => {
        try {
            if (funnelId) {
                await updateFunnel(funnelId, values);
            } else {
                await createFunnel(values);
            }
            toast.success(i18n.t("kanban.funnels.toasts.success"));
            if (reload) reload();
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {funnelId
                        ? i18n.t("kanban.funnels.modal.edit")
                        : i18n.t("kanban.funnels.modal.add")}
                </DialogTitle>
                <Formik
                    initialValues={funnel}
                    enableReinitialize={true}
                    validationSchema={FunnelSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setTimeout(() => {
                            handleSaveFunnel(values);
                            setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.funnels.modal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    label={i18n.t("kanban.funnels.modal.form.description")}
                                    name="description"
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {funnelId
                                        ? i18n.t("global.buttons.save")
                                        : i18n.t("global.buttons.add")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default FunnelModal;
